import PageHeader from "@/components/PageHeader";
import { Trash2, Mail, AlertTriangle, ArrowRight } from "lucide-react";

export default function DeleteAccount() {
  return (
    <div className="bg-white text-black min-h-screen">
      <PageHeader
        overline="Data Controls"
        title="Delete"
        highlight="Account"
        subtitle="Last Updated: August 9, 2026. Learn how to permanently delete your Grooh account and understand what data is removed."
        testid="delete-account-header"
      />

      <section className="py-12 md:py-20 border-t border-black/10">
        <div className="container-x max-w-4xl mx-auto">
          {/* Main Layout: Instructions Card */}
          <div className="bg-slate-50 border border-slate-100 p-8 md:p-12 rounded-3xl mb-16">
            <div className="max-w-2xl">
              <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center mb-6 text-red-600">
                <Mail size={24} />
              </div>
              <h3 className="font-heading font-black text-2xl md:text-3xl mb-4 text-black">How to Request Account Deletion</h3>
              <p className="text-sm md:text-base text-black/75 mb-8 leading-relaxed">
                To request permanent deletion of your Grooh account and all associated personal data, please send an email to our support team.
              </p>
              
              <div className="bg-white border border-black/10 p-6 md:p-8 rounded-2xl space-y-4 text-sm md:text-base text-black/85">
                <p>
                  Send an email to:{" "}
                  <a href="mailto:hello@grooh.app" className="text-[#0033A2] font-semibold underline hover:text-blue-800">
                    hello@grooh.app
                  </a>
                </p>
                
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-black/60 mb-3">
                    Please include the following details in your email:
                  </p>
                  <ul className="space-y-2 list-disc pl-5 text-sm text-black/70">
                    <li><strong>Full Name:</strong> The name associated with your account profile</li>
                    <li><strong>Registered Email:</strong> Your email address used during sign up</li>
                    <li><strong>Registered Mobile Number:</strong> The phone number linked to your profile</li>
                    <li><strong>Society Details:</strong> Your Housing Society name and Flat / Unit number</li>
                  </ul>
                </div>
              </div>

              <p className="mt-8 text-xs text-black/50">
                * Note: Account deletion requests are processed manually. Once verified, your account will be deactivated and permanently deleted within 7 business days.
              </p>
            </div>
          </div>

          {/* Bottom layout: Data Policy Details */}
          <div className="grid md:grid-cols-2 gap-12 md:gap-16 pt-12 border-t border-black/10">
            
            {/* What is Deleted */}
            <div className="space-y-4">
              <h4 className="font-heading font-black text-xl text-black flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-red-600"></span> What is Deleted?
              </h4>
              <p className="text-sm text-black/70 leading-relaxed">
                When your account deletion is finalized, we permanently wipe all of your personally identifiable information (PII) from our active databases:
              </p>
              <ul className="space-y-3 text-sm text-black/80 list-disc pl-5">
                <li><strong>Personal Profile:</strong> Name, profile photo, email address, password/auth tokens, and phone number.</li>
                <li><strong>Household Data:</strong> Registrations of family members associated with your unit.</li>
                <li><strong>Vehicle Data:</strong> All vehicle registration numbers and parking details linked to your unit.</li>
                <li><strong>Active Support Requests:</strong> Pending helpdesk complaints or maintenance feedback.</li>
              </ul>
            </div>

            {/* What is Retained */}
            <div className="space-y-4">
              <h4 className="font-heading font-black text-xl text-black flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-slate-400"></span> What is Retained?
              </h4>
              <p className="text-sm text-black/70 leading-relaxed">
                To comply with audits and cooperative housing society financial rules, certain operational records are archived and anonymized rather than deleted:
              </p>
              <ul className="space-y-3 text-sm text-black/80 list-disc pl-5">
                <li><strong>Maintenance Ledger:</strong> Invoices and transaction records are kept for society audits but are fully unlinked from your profile (anonymized).</li>
                <li><strong>Noticeboard History:</strong> Notices published by you (if you were an admin) remain visible for historical record, marked as &quot;Deactivated User&quot;.</li>
                <li><strong>Voting Logs:</strong> Cast votes are anonymized and retained to preserve the mathematical integrity of historical poll results.</li>
              </ul>
            </div>

          </div>

        </div>
      </section>
    </div>
  );
}
